import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Image, Upload, Trash2, ExternalLink, Package, Store, Eye, Paintbrush, FolderPlus, Layers, Coins, Plus, X } from "lucide-react";

const API = `${window.location.origin}/api`;

const NIVEIS_INFO = [
  { id: "bronze",   nome: "Bronze",   emoji: "🥉", cor: "from-amber-600 to-orange-700" },
  { id: "prata",    nome: "Prata",    emoji: "🥈", cor: "from-slate-300 to-slate-500" },
  { id: "ouro",     nome: "Ouro",     emoji: "🥇", cor: "from-yellow-300 to-yellow-500" },
  { id: "platinum", nome: "Platinum", emoji: "⭐", cor: "from-cyan-200 to-cyan-400" },
  { id: "diamante", nome: "Diamante", emoji: "💎", cor: "from-sky-300 to-blue-500" },
];

const formatMoeda = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

const TEMAS_CORES = [
  { nome: "Esmeralda",   primary: "#059669", secondary: "#0d9488", botao: "#059669" },
  { nome: "Azul Oceano", primary: "#2563eb", secondary: "#0ea5e9", botao: "#2563eb" },
  { nome: "Violeta",     primary: "#7c3aed", secondary: "#a855f7", botao: "#7c3aed" },
  { nome: "Rosa",        primary: "#db2777", secondary: "#ec4899", botao: "#db2777" },
  { nome: "Laranja",     primary: "#ea580c", secondary: "#f59e0b", botao: "#ea580c" },
  { nome: "Grafite",     primary: "#1e293b", secondary: "#475569", botao: "#0f172a" },
  { nome: "Coral",       primary: "#f43f5e", secondary: "#fb7185", botao: "#e11d48" },
  { nome: "Turquesa",    primary: "#0891b2", secondary: "#06b6d4", botao: "#0e7490" },
  { nome: "Lima",        primary: "#65a30d", secondary: "#84cc16", botao: "#4d7c0f" },
  { nome: "Indigo",      primary: "#4338ca", secondary: "#6366f1", botao: "#4338ca" },
  { nome: "Magenta",     primary: "#a21caf", secondary: "#c026d3", botao: "#a21caf" },
  { nome: "Dourado",     primary: "#b45309", secondary: "#d97706", botao: "#b45309" },
  { nome: "Café",        primary: "#78350f", secondary: "#92400e", botao: "#78350f" },
  { nome: "Pôr-do-sol",  primary: "#f97316", secondary: "#fb923c", botao: "#ea580c" },
  { nome: "Floresta",    primary: "#166534", secondary: "#15803d", botao: "#166534" },
  { nome: "Vinho",       primary: "#7f1d1d", secondary: "#991b1b", botao: "#7f1d1d" },
  { nome: "Pastel Mint", primary: "#10b981", secondary: "#34d399", botao: "#059669" },
  { nome: "Pastel Lilás",primary: "#a78bfa", secondary: "#c4b5fd", botao: "#8b5cf6" },
  { nome: "Preto Total", primary: "#0a0a0a", secondary: "#262626", botao: "#171717" },
  { nome: "Branco Limpo",primary: "#475569", secondary: "#64748b", botao: "#0f172a" },
];

export default function LojaOnlinePage({ embedded = false }) {
  const [config, setConfig] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [tab, setTab] = useState("banners"); // banners | produtos | tema
  const [busca, setBusca] = useState("");
  const bannerInputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfg, prods] = await Promise.all([
        axios.get(`${API}/loja/config`),
        axios.get(`${API}/produtos`)
      ]);
      setConfig(cfg.data);
      setProdutos(Array.isArray(prods.data) ? prods.data : []);
    } catch {
      toast.error("Erro ao carregar loja");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const abrirLoja = () => {
    // Abre o link EXCLUSIVO da loja do assinante (mesmo link compartilhado em Configurações)
    // Sem o uid, cairia em "Loja não encontrada" agora que cada tenant tem URL própria.
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const uid = u?.id || "";
    const url = uid
      ? `${window.location.origin}/loja?uid=${uid}`
      : `${window.location.origin}/loja`;
    window.open(url, "_blank");
  };

  // ========= BANNERS =========
  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/loja/banner`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Banner enviado!");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar banner");
    } finally {
      setUploadingBanner(false);
      if (bannerInputRef.current) bannerInputRef.current.value = "";
    }
  };

  const removerBanner = async (id) => {
    if (!window.confirm("Remover este banner?")) return;
    try {
      await axios.delete(`${API}/loja/banner/${id}`);
      toast.success("Banner removido");
      loadAll();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  // ========= TEMA =========
  const salvarTema = async (tema) => {
    try {
      await axios.put(`${API}/loja/config`, { tema });
      toast.success("Tema atualizado!");
      setConfig(c => ({ ...c, tema }));
    } catch {
      toast.error("Erro ao salvar tema");
    }
  };

  // ========= PRODUTOS =========
  const produtosFiltrados = produtos.filter(p => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (p.nome || "").toLowerCase().includes(q) || (p.codigo_sku || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="loja-online-page">
      {!embedded && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Store className="h-6 w-6 text-fuchsia-600" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Work Sans" }}>Loja Online</h1>
          </div>
          <Button onClick={abrirLoja} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="btn-ver-loja">
            <Eye className="h-4 w-4" /> Ver Loja <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end">
          <Button onClick={abrirLoja} variant="outline" size="sm" className="gap-2" data-testid="btn-ver-loja">
            <Eye className="h-3.5 w-3.5" /> Ver Loja <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          { id: "banners", label: "Banners", icon: Image },
          { id: "categorias", label: "Categorias", icon: FolderPlus },
          { id: "produtos", label: "Fotos & Descrições", icon: Package },
          { id: "kits", label: "Kits", icon: Layers },
          { id: "niveis", label: "Níveis & Cashback", icon: Coins },
          { id: "tema", label: "Tema", icon: Paintbrush },
        ].map(t => {
          const I = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`shrink-0 px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === t.id ? "border-fuchsia-600 text-fuchsia-700" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            >
              <I className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ===== TAB BANNERS ===== */}
      {tab === "banners" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Banners da Loja</CardTitle>
            <p className="text-xs text-gray-500">Imagens exibidas no topo da loja virtual. Recomendado: 1200x400px, máx 5MB.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg py-6 cursor-pointer hover:bg-fuchsia-50 transition-colors" data-testid="upload-banner-area">
              <input type="file" accept="image/*" hidden onChange={handleBannerUpload} ref={bannerInputRef} />
              <Upload className="h-5 w-5 text-fuchsia-600" />
              <span className="text-sm font-medium text-fuchsia-700">{uploadingBanner ? "Enviando..." : "Adicionar banner"}</span>
            </label>
            {config && config.banners?.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-4">Nenhum banner adicionado</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {config?.banners?.map((b, idx) => (
                <div key={b.id} className="relative group rounded-lg overflow-hidden border" data-testid={`banner-${b.id}`}>
                  <img src={b.url} alt={`Banner ${idx + 1}`} className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2">
                    <Button size="sm" variant="destructive" onClick={() => removerBanner(b.id)} className="opacity-0 group-hover:opacity-100 gap-1" data-testid={`btn-del-banner-${b.id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Remover
                    </Button>
                  </div>
                  <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">#{idx + 1}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== TAB PRODUTOS ===== */}
      {tab === "produtos" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fotos & Descrições dos Produtos</CardTitle>
            <Input placeholder="Buscar produto..." value={busca} onChange={e => setBusca(e.target.value)} className="mt-2" data-testid="input-busca-produto-loja" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {produtosFiltrados.map(p => (
                <ProdutoEditCard key={p.id} produto={p} onReload={loadAll} />
              ))}
            </div>
            {produtosFiltrados.length === 0 && !loading && (
              <p className="text-center text-gray-400 py-8">Nenhum produto</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== TAB CATEGORIAS ===== */}
      {tab === "categorias" && <CategoriasTab />}

      {/* ===== TAB KITS ===== */}
      {tab === "kits" && <KitsTab produtos={produtos} />}

      {/* ===== TAB NÍVEIS & CASHBACK ===== */}
      {tab === "niveis" && <NiveisTab config={config} setConfig={setConfig} />}

      {/* ===== TAB TEMA ===== */}
      {tab === "tema" && <TemaTab config={config} setConfig={setConfig} salvarTema={salvarTema} />}
    </div>
  );
}

// Card editável de produto (upload foto + descricao_loja)
function ProdutoEditCard({ produto, onReload }) {
  const p = produto;
  const [uploading, setUploading] = useState(false);
  const [desc, setDesc] = useState(p.descricao_loja || "");
  const [salvandoDesc, setSalvandoDesc] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/produtos/${p.id}/imagem`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto atualizada!");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removerFoto = async () => {
    if (!window.confirm("Remover foto deste produto?")) return;
    try {
      await axios.delete(`${API}/produtos/${p.id}/imagem`);
      toast.success("Foto removida");
      onReload();
    } catch { toast.error("Erro"); }
  };

  const salvarDesc = async () => {
    setSalvandoDesc(true);
    try {
      await axios.patch(`${API}/produtos/${p.id}/loja`, { descricao_loja: desc });
      toast.success("Descrição salva");
    } catch { toast.error("Erro"); }
    finally { setSalvandoDesc(false); }
  };

  return (
    <Card className="overflow-hidden" data-testid={`prod-edit-${p.id}`}>
      <div className="aspect-square bg-gray-100 relative flex items-center justify-center">
        {p.imagem_url ? (
          <img src={p.imagem_url} alt={p.nome} className="w-full h-full object-cover" />
        ) : (
          <Package className="h-10 w-10 text-gray-300" />
        )}
        <div className="absolute bottom-1 right-1 flex gap-1">
          <label className="cursor-pointer bg-white/90 hover:bg-white rounded p-1.5 shadow" title="Enviar foto" data-testid={`upload-foto-${p.id}`}>
            <input type="file" accept="image/*" hidden onChange={handleUpload} ref={fileRef} />
            {uploading ? <span className="text-[10px] font-bold px-1">...</span> : <Upload className="h-3.5 w-3.5 text-gray-700" />}
          </label>
          {p.imagem_url && (
            <button onClick={removerFoto} className="bg-white/90 hover:bg-white rounded p-1.5 shadow" title="Remover">
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </button>
          )}
        </div>
      </div>
      <CardContent className="p-3 space-y-2">
        <p className="text-[10px] text-gray-400 uppercase">{p.codigo_sku}</p>
        <p className="text-xs font-semibold line-clamp-2 min-h-[2.5rem]">{p.nome}</p>
        <p className="text-sm font-bold text-emerald-700">{formatMoeda(p.preco_venda)}</p>
        <div>
          <textarea
            value={desc}
            onChange={e => setDesc(e.target.value)}
            onBlur={salvarDesc}
            rows={2}
            placeholder="Descrição (opcional)"
            className="w-full text-xs border rounded p-1.5 resize-none"
            data-testid={`desc-loja-${p.id}`}
          />
          {salvandoDesc && <p className="text-[9px] text-gray-400">Salvando...</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ========== CATEGORIAS ==========
function CategoriasTab() {
  const [cats, setCats] = useState([]);
  const [nova, setNova] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/loja/categorias`); setCats(data); } catch { toast.error("Erro"); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const nome = (nova || "").trim();
    if (!nome) return;
    setLoading(true);
    try {
      await axios.post(`${API}/loja/categorias`, { nome });
      setNova("");
      toast.success("Categoria criada!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  const del = async (id, nome) => {
    if (!window.confirm(`Remover categoria "${nome}"?`)) return;
    try { await axios.delete(`${API}/loja/categorias/${id}`); toast.success("Removida"); load(); }
    catch { toast.error("Erro"); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Categorias da Loja</CardTitle>
        <p className="text-xs text-gray-500">Categorias visíveis como filtros na loja virtual. Os produtos usam essas categorias no cadastro.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input placeholder="Nova categoria (ex: PERFUMES)" value={nova} onChange={e => setNova(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && add()} data-testid="input-nova-categoria" />
          <Button onClick={add} disabled={loading} className="gap-1 bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="btn-add-categoria">
            <Plus className="h-4 w-4" /> Adicionar
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {cats.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-white" data-testid={`cat-${c.id}`}>
              <span className="text-sm font-medium truncate">{c.nome}</span>
              <button onClick={() => del(c.id, c.nome)} className="p-1 rounded bg-red-100 text-red-600 hover:bg-red-200" data-testid={`btn-del-cat-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        {cats.length === 0 && <p className="text-center text-xs text-gray-400 py-4">Nenhuma categoria</p>}
      </CardContent>
    </Card>
  );
}

// ========== KITS ==========
function KitsTab({ produtos }) {
  const [kits, setKits] = useState([]);
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [k, c] = await Promise.all([axios.get(`${API}/loja/kits`), axios.get(`${API}/loja/categorias`)]);
      setKits(k.data); setCats(c.data);
    } catch { toast.error("Erro"); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Remover kit?")) return;
    try { await axios.delete(`${API}/loja/kits/${id}`); toast.success("Removido"); load(); }
    catch { toast.error("Erro"); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Kits (combos de produtos)</CardTitle>
            <p className="text-xs text-gray-500">Monte kits com 2+ produtos. Estoque baixa de cada item ao aprovar o pedido.</p>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} className="gap-1 bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="btn-novo-kit">
            <Plus className="h-4 w-4" /> Novo Kit
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {kits.map(k => (
            <div key={k.id} className={`rounded-lg border p-3 bg-white ${!k.ativo && 'opacity-50'}`} data-testid={`kit-${k.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{k.nome}</p>
                  <p className="text-xs text-gray-500">{k.categoria || 'Sem categoria'}</p>
                  <p className="text-base font-bold text-emerald-700 mt-1">R$ {Number(k.preco).toFixed(2).replace('.', ',')}</p>
                  <p className="text-[10px] text-gray-400">{(k.itens || []).length} produtos · {(k.itens || []).reduce((a, x) => a + (x.quantidade || 1), 0)} unidades</p>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => { setEditing(k); setShowForm(true); }} className="p-1 rounded bg-blue-100 text-blue-600 hover:bg-blue-200" data-testid={`btn-edit-kit-${k.id}`}>✏️</button>
                  <button onClick={() => del(k.id)} className="p-1 rounded bg-red-100 text-red-600 hover:bg-red-200"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
          {kits.length === 0 && <p className="col-span-full text-center text-gray-400 py-8 text-sm">Nenhum kit criado</p>}
        </div>
        {showForm && <KitForm produtos={produtos} cats={cats} kit={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { load(); setShowForm(false); setEditing(null); }} />}
      </CardContent>
    </Card>
  );
}

function KitForm({ produtos, cats, kit, onClose, onSaved }) {
  const [nome, setNome] = useState(kit?.nome_custom || "");
  const [categoria, setCategoria] = useState(kit?.categoria || "");
  const [preco, setPreco] = useState(kit?.preco?.toString() || "");
  const [precoClienteFinal, setPrecoClienteFinal] = useState(kit?.preco_cliente_final != null ? kit.preco_cliente_final.toString() : "");
  const [itens, setItens] = useState(kit?.itens || []);
  const [ativo, setAtivo] = useState(kit?.ativo !== false);
  const [saving, setSaving] = useState(false);
  const [busca, setBusca] = useState("");
  const [imagemUrl, setImagemUrl] = useState(kit?.imagem_url || null);
  const [uploadingImg, setUploadingImg] = useState(false);

  const addItem = (p) => {
    if (itens.find(x => x.produto_id === p.id)) { toast.warning("Produto já adicionado"); return; }
    setItens([...itens, { produto_id: p.id, produto_nome: p.nome, quantidade: 1 }]);
  };
  const updQtd = (idx, v) => { const n = [...itens]; n[idx].quantidade = Math.max(1, parseInt(v) || 1); setItens(n); };
  const remove = (idx) => setItens(itens.filter((_, i) => i !== idx));

  const handleUploadFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Precisa de kit já existente para upload. Se for criação, salva primeiro.
    let kitId = kit?.id;
    if (!kitId) {
      toast.info("Salvando kit antes de enviar foto...");
      if (itens.length < 2) { toast.error("Adicione 2+ produtos antes"); return; }
      if (!preco || parseFloat(preco) <= 0) { toast.error("Informe o preço"); return; }
      try {
        const payload = {
          nome: nome.trim(), categoria: categoria || null, preco: parseFloat(preco),
          preco_cliente_final: precoClienteFinal !== "" ? parseFloat(precoClienteFinal) : null,
          itens: itens.map(x => ({ produto_id: x.produto_id, quantidade: x.quantidade })),
          ativo
        };
        const { data } = await axios.post(`${API}/loja/kits`, payload);
        kitId = data.id;
      } catch (err) { toast.error("Erro ao salvar kit"); return; }
    }
    setUploadingImg(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/loja/kits/${kitId}/imagem`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setImagemUrl(data.imagem_url);
      toast.success("Foto enviada!");
      if (!kit) { onSaved(); } // se era novo, fecha form e recarrega
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setUploadingImg(false); }
  };

  const salvar = async () => {
    if (itens.length < 2) { toast.error("Kit precisa de 2+ produtos"); return; }
    if (!preco || parseFloat(preco) <= 0) { toast.error("Informe o preço"); return; }
    setSaving(true);
    try {
      const payload = {
        nome: nome.trim(),
        categoria: categoria || null,
        preco: parseFloat(preco),
        preco_cliente_final: precoClienteFinal !== "" ? parseFloat(precoClienteFinal) : null,
        itens: itens.map(x => ({ produto_id: x.produto_id, quantidade: x.quantidade })),
        ativo
      };
      if (kit) await axios.put(`${API}/loja/kits/${kit.id}`, payload);
      else await axios.post(`${API}/loja/kits`, payload);
      toast.success("Kit salvo");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const produtosFiltrados = produtos.filter(p => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (p.nome || "").toLowerCase().includes(q) || (p.codigo_sku || "").toLowerCase().includes(q);
  });
  const totalItens = itens.reduce((a, x) => a + (x.quantidade || 1), 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <h3 className="font-bold text-base">{kit ? 'Editar Kit' : 'Novo Kit'}</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Foto do kit */}
          <div className="flex items-start gap-3">
            <div className="w-28 h-28 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden border flex items-center justify-center relative">
              {imagemUrl ? (
                <img src={imagemUrl} alt="Kit" className="w-full h-full object-cover" />
              ) : (
                <Package className="h-10 w-10 text-gray-300" />
              )}
              {uploadingImg && <span className="absolute inset-0 bg-white/70 flex items-center justify-center text-xs font-bold">...</span>}
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold">Foto do Kit</label>
              <label className="block cursor-pointer border-2 border-dashed rounded-lg py-3 px-3 hover:bg-fuchsia-50 transition-colors text-center mt-1" data-testid="upload-kit-foto">
                <input type="file" accept="image/*" hidden onChange={handleUploadFoto} />
                <Upload className="h-4 w-4 inline mr-1 text-fuchsia-600" />
                <span className="text-xs font-medium text-fuchsia-700">{imagemUrl ? "Trocar foto" : "Enviar foto"}</span>
              </label>
              <p className="text-[10px] text-gray-400 mt-1">Máx 5MB. Recomendado 600x600px.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold">Nome customizado (opcional)</label>
              <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Tratamento Completo" data-testid="kit-nome" />
              <p className="text-[10px] text-gray-400 mt-1">Nome final: <strong>KIT {totalItens}pcs — {nome || "(auto)"}</strong></p>
            </div>
            <div>
              <label className="text-xs font-semibold">Categoria</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" data-testid="kit-categoria">
                <option value="">-- Sem categoria --</option>
                {cats.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Preço SALÃO/REVENDA (R$) *</label>
              <Input type="number" step="0.01" value={preco} onChange={e => setPreco(e.target.value)} data-testid="kit-preco" className="border-purple-300" />
            </div>
            <div>
              <label className="text-xs font-semibold">Preço CLIENTE FINAL (R$)</label>
              <Input type="number" step="0.01" value={precoClienteFinal} onChange={e => setPrecoClienteFinal(e.target.value)} placeholder="Opcional" data-testid="kit-preco-cliente-final" className="border-pink-300" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} id="kit-ativo" />
              <label htmlFor="kit-ativo" className="text-sm">Kit ativo (visível na loja)</label>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold mb-2">Itens do kit ({itens.length}) — {totalItens} unidades</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto mb-3">
              {itens.map((it, i) => (
                <div key={it.produto_id} className="flex items-center gap-2 p-2 border rounded bg-gray-50">
                  <span className="flex-1 text-xs font-medium truncate">{it.produto_nome}</span>
                  <Input type="number" min="1" value={it.quantidade} onChange={e => updQtd(i, e.target.value)} className="w-20 h-8" />
                  <button onClick={() => remove(i)} className="p-1 rounded bg-red-100 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {itens.length === 0 && <p className="text-xs text-gray-400 text-center py-3">Adicione 2+ produtos abaixo</p>}
            </div>
            <Input placeholder="Buscar produto..." value={busca} onChange={e => setBusca(e.target.value)} className="mb-2" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto border rounded p-2 bg-white">
              {produtosFiltrados.map(p => (
                <button key={p.id} onClick={() => addItem(p)} className="text-left text-xs p-1.5 rounded hover:bg-emerald-50 border" data-testid={`add-prod-kit-${p.id}`}>
                  <p className="font-medium truncate">{p.nome}</p>
                  <p className="text-[10px] text-gray-400">R$ {Number(p.preco_venda).toFixed(2)}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-3 border-t">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={salvar} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="btn-salvar-kit">
              {saving ? "Salvando..." : "Salvar Kit"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ========== NÍVEIS & CASHBACK ==========
function NiveisTab({ config, setConfig }) {
  const [valores, setValores] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const cb = config?.cashback_por_nivel || {};
    setValores({
      bronze: cb.bronze || 0,
      prata: cb.prata || 0,
      ouro: cb.ouro || 0,
      platinum: cb.platinum || 0,
      diamante: cb.diamante || 0,
    });
  }, [config]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const { data } = await axios.put(`${API}/loja/config`, { cashback_por_nivel: valores });
      setConfig(data);
      toast.success("Cashback atualizado!");
    } catch { toast.error("Erro"); }
    finally { setSalvando(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Níveis de Cliente & Cashback</CardTitle>
        <p className="text-xs text-gray-500">Defina o % de cashback por nível. Crédito acumula no saldo da cliente ao aprovar pedido.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {NIVEIS_INFO.map(n => (
            <div key={n.id} className="rounded-lg border-2 p-3 text-center bg-gradient-to-b from-white to-gray-50" data-testid={`nivel-card-${n.id}`}>
              <div className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-br ${n.cor} flex items-center justify-center text-6xl shadow-inner`}>
                {n.emoji}
              </div>
              <p className="font-bold text-sm mt-2">{n.nome}</p>
              <div className="mt-2">
                <label className="text-[10px] text-gray-500">% Cashback</label>
                <div className="flex items-center gap-1">
                  <Input
                    type="number" step="0.1" min="0" max="50"
                    value={valores[n.id] ?? 0}
                    onChange={e => setValores({ ...valores, [n.id]: parseFloat(e.target.value) || 0 })}
                    className="text-center"
                    data-testid={`cashback-${n.id}`}
                  />
                  <span className="text-sm font-bold">%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={salvar} disabled={salvando} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="btn-salvar-cashback">
          {salvando ? "Salvando..." : "Salvar Cashback"}
        </Button>
      </CardContent>
    </Card>
  );
}


// ===== Tab Tema (paleta + cores customizadas + velocidade banner) =====
function TemaTab({ config, setConfig, salvarTema }) {
  const tema = config?.tema || {};
  const [primary, setPrimary] = useState(tema.primary || "#059669");
  const [secondary, setSecondary] = useState(tema.secondary || "#0d9488");
  const [corBotao, setCorBotao] = useState(tema.cor_botao_comprar || "#059669");
  const [bannerSpeed, setBannerSpeed] = useState(tema.banner_speed_ms || 3333);
  const [categoriasLayout, setCategoriasLayout] = useState(tema.categorias_layout || "chips");
  const [salvando, setSalvando] = useState(false);

  // Sincronizar com config quando carregar/mudar
  useEffect(() => {
    setPrimary(tema.primary || "#059669");
    setSecondary(tema.secondary || "#0d9488");
    setCorBotao(tema.cor_botao_comprar || tema.primary || "#059669");
    setBannerSpeed(tema.banner_speed_ms || 3333);
    setCategoriasLayout(tema.categorias_layout || "chips");
  }, [config?.tema?.primary, config?.tema?.secondary, config?.tema?.cor_botao_comprar, config?.tema?.banner_speed_ms, config?.tema?.categorias_layout, tema]);

  const aplicarPreset = async (t) => {
    setPrimary(t.primary);
    setSecondary(t.secondary);
    setCorBotao(t.botao);
    setSalvando(true);
    await salvarTema({
      nome: t.nome,
      primary: t.primary,
      secondary: t.secondary,
      cor_botao_comprar: t.botao,
      banner_speed_ms: bannerSpeed,
      categorias_layout: categoriasLayout,
    });
    setSalvando(false);
  };

  const salvarPersonalizado = async () => {
    setSalvando(true);
    await salvarTema({
      nome: "Personalizado",
      primary,
      secondary,
      cor_botao_comprar: corBotao,
      banner_speed_ms: Number(bannerSpeed) || 3333,
      categorias_layout: categoriasLayout,
    });
    setSalvando(false);
  };

  return (
    <div className="space-y-4">
      {/* Paleta de presets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Paleta de Cores</CardTitle>
          <p className="text-xs text-gray-500">Toque em um tema para aplicar instantaneamente.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {TEMAS_CORES.map(t => {
              const ativo = config?.tema?.primary === t.primary && config?.tema?.secondary === t.secondary;
              return (
                <button
                  key={t.nome}
                  onClick={() => aplicarPreset(t)}
                  disabled={salvando}
                  className={`rounded-lg p-3 border-2 text-left transition-all ${ativo ? "border-fuchsia-600 ring-2 ring-fuchsia-200" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid={`tema-${t.nome.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-7 h-7 rounded" style={{ backgroundColor: t.primary }} title="Cabeçalho 1" />
                    <div className="w-7 h-7 rounded" style={{ backgroundColor: t.secondary }} title="Cabeçalho 2" />
                    <div className="w-7 h-7 rounded border" style={{ backgroundColor: t.botao }} title="Botão Comprar" />
                  </div>
                  <p className="text-sm font-semibold">{t.nome}</p>
                  {ativo && <p className="text-[10px] text-fuchsia-600 font-bold mt-1">ATIVO</p>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Cores personalizadas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cores Personalizadas</CardTitle>
          <p className="text-xs text-gray-500">Escolha cores específicas (qualquer HEX).</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ColorPickerField
              label="Cor primária (cabeçalho)"
              value={primary}
              onChange={setPrimary}
              testid="picker-primary"
            />
            <ColorPickerField
              label="Cor secundária (gradiente)"
              value={secondary}
              onChange={setSecondary}
              testid="picker-secondary"
            />
            <ColorPickerField
              label="Botão Comprar / Adicionar"
              value={corBotao}
              onChange={setCorBotao}
              testid="picker-botao-comprar"
            />
          </div>

          {/* Preview do botão */}
          <div className="pt-2 border-t">
            <p className="text-[10px] text-gray-500 uppercase mb-2">Pré-visualização</p>
            <div
              className="rounded-xl p-4 text-white"
              style={{ background: `linear-gradient(to right, ${primary}, ${secondary})` }}
            >
              <p className="text-sm font-bold mb-2">Cabeçalho da Loja</p>
              <button
                className="text-white font-bold py-2 px-4 rounded-md text-sm shadow"
                style={{ backgroundColor: corBotao }}
              >
                + Adicionar ao carrinho
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Velocidade do banner */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Velocidade do Banner</CardTitle>
          <p className="text-xs text-gray-500">Tempo entre cada slide do carrossel da loja.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="1000"
              max="15000"
              step="500"
              value={bannerSpeed}
              onChange={e => setBannerSpeed(Number(e.target.value))}
              className="flex-1 accent-fuchsia-600"
              data-testid="slider-banner-speed"
            />
            <div className="text-sm font-bold text-fuchsia-700 w-20 text-right" data-testid="banner-speed-value">
              {(bannerSpeed / 1000).toFixed(1)}s
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {[2000, 3000, 5000, 7000, 10000].map(v => (
              <button
                key={v}
                onClick={() => setBannerSpeed(v)}
                className={`text-xs px-3 py-1 rounded-full border transition ${bannerSpeed === v ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white text-gray-700 border-gray-300 hover:border-fuchsia-400"}`}
                data-testid={`preset-speed-${v}`}
              >
                {v / 1000}s
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-500">
            Mais rápido = banners trocam com mais frequência. Recomendado: 3-5s.
          </p>
        </CardContent>
      </Card>

      {/* Visualização das Categorias */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Visualização das Categorias</CardTitle>
          <p className="text-xs text-gray-500">Como os clientes verão a lista de categorias no celular.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { id: "chips", label: "Chips horizontais", desc: "Arrastar para o lado (atual)" },
              { id: "grade2", label: "Grade 2 colunas", desc: "Todas visíveis, 2 por linha" },
              { id: "grade3", label: "Grade 3 colunas", desc: "Todas visíveis, 3 por linha" },
              { id: "dropdown", label: "Menu suspenso", desc: "Select compacto" },
              { id: "expansivel", label: "Lista expansível", desc: "Abre ao tocar, fecha ao escolher" },
            ].map(opt => {
              const ativo = categoriasLayout === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setCategoriasLayout(opt.id)}
                  className={`text-left rounded-lg p-3 border-2 transition-all ${ativo ? "border-fuchsia-600 ring-2 ring-fuchsia-200 bg-fuchsia-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                  data-testid={`layout-cat-${opt.id}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">{opt.label}</p>
                    {ativo && <span className="text-[10px] text-fuchsia-600 font-bold">ATIVO</span>}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</p>
                  <CategoriasLayoutPreview variant={opt.id} />
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-500">
            Clique em <strong>Salvar Personalização</strong> abaixo para aplicar.
          </p>
        </CardContent>
      </Card>

      {/* Botão salvar */}
      <Button
        onClick={salvarPersonalizado}
        disabled={salvando}
        className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
        data-testid="btn-salvar-tema-personalizado"
      >
        {salvando ? "Salvando..." : "Salvar Personalização"}
      </Button>
    </div>
  );
}

function ColorPickerField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-700">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
          data-testid={`${testid}-color`}
        />
        <Input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="font-mono text-xs uppercase"
          maxLength={7}
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}


// Mini-preview ilustrativo de cada layout de categorias
function CategoriasLayoutPreview({ variant }) {
  const items = ["Todas", "Shampoos", "Finalizadores", "Kits"];
  if (variant === "dropdown") {
    return (
      <div className="mt-2 rounded border border-gray-200 bg-white px-2 py-1 text-[10px] text-gray-500 flex items-center justify-between">
        <span>Shampoos</span>
        <span>▾</span>
      </div>
    );
  }
  if (variant === "expansivel") {
    return (
      <div className="mt-2 rounded border border-gray-200 bg-white px-2 py-1 text-[10px] text-gray-500 flex items-center justify-between">
        <span>Categoria: <strong>Shampoos</strong></span>
        <span>▾</span>
      </div>
    );
  }
  if (variant === "grade2" || variant === "grade3") {
    const cols = variant === "grade3" ? "grid-cols-3" : "grid-cols-2";
    return (
      <div className={`grid ${cols} gap-1 mt-2`}>
        {items.map(i => (
          <div key={i} className="rounded bg-white border border-gray-200 text-[9px] text-gray-500 text-center py-1 truncate">{i}</div>
        ))}
      </div>
    );
  }
  // chips
  return (
    <div className="flex gap-1 mt-2 overflow-hidden">
      {items.map(i => (
        <div key={i} className="shrink-0 rounded-full bg-white border border-gray-200 text-[9px] text-gray-500 px-2 py-0.5">{i}</div>
      ))}
    </div>
  );
}
